import z from "zod";
import { OrgTeamFilterParamsSchema } from "rdx/modules/enterprisePersonnel/schemas";
import ZodSchemaUtil from "util/schema-helpers/ZodSchemaUtil";
import { TeamTypeEnum } from "rdx/modules/team/enums";
import { AmbassadorFilterParamsSchema } from "rdx/modules/team/schemas";
import { EarningsAnalyticsTimeFrameEnum } from "rdx/modules/earnings/enums";
import { TeamMembersFilterParamsSchema } from "rdx/modules/teamIntelligence/schemas";
import {
  OrgCompletedFilterParamsSchema,
  OrgPostSalesFilterParamsSchema,
  OrgPreSalesFilterParamsSchema,
} from "../enterpriseProjects/schemas";
import { CsvJobStateEnum, CsvJobNameEnum, CsvReportNameEnum, CsvJobNameKeyEnum } from "./enums";

export const CsvJobStateSchema = z.nativeEnum(CsvJobStateEnum);
export const CsvJobNameSchema = z.nativeEnum(CsvJobNameEnum);
export const CsvReportNameSchema = z.nativeEnum(CsvReportNameEnum);

export const GetTeamUsersCsvParamsSchema = z.object({
  filterParams: OrgTeamFilterParamsSchema,
  searchedUserId: z.union([z.number(), z.null()]),
  isEnterprise: z.boolean(),
  teamType: z.nativeEnum(TeamTypeEnum),
});

export const GetOrgProjectsCsvParamsSchema = z.object({
  filterParams: OrgPreSalesFilterParamsSchema.or(OrgPostSalesFilterParamsSchema).or(OrgCompletedFilterParamsSchema),
  tab: ZodSchemaUtil.makeStringLiteralUnion(["pre_sales", "post_sales", "completed"]),
});

export const CsvJobSchema = z.object({
  results: z.boolean().optional(),
  state: CsvJobStateSchema.nullable(),
  ts: z.string(),
});

export const CsvReportSchema = z.object({
  createdAt: z.string(),
  id: z.number(),
  jobName: CsvJobNameSchema.nullable(),
  name: z.union([
    ZodSchemaUtil.makeStringLiteralUnion([
      CsvReportNameEnum.TEAM_USERS,
      CsvReportNameEnum.ORG_TEAM_USERS,
      CsvReportNameEnum.ORG_PRE_SALES_PROJECTS,
      CsvReportNameEnum.ORG_POST_SALES_PROJECTS,
      CsvReportNameEnum.ORG_COMPLETED_PROJECTS,
      CsvReportNameEnum.TEAM_INTELLIGENCE,
    ]),
    z.string().regex(/^EarningsIntelligence-\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/),
  ]),
  url: z.string().nullable(),
  jobStatus: z.string().nullish(),
});

export const GetCsvReportByIdResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    report: CsvReportSchema,
  }),
});

export const TeamAmbassadorsCsvSchema = CsvReportSchema.extend({
  jobs: z.object({
    [CsvJobNameKeyEnum.TEAM_USERS]: CsvJobSchema,
  }),
  name: ZodSchemaUtil.makeStringLiteralUnion([CsvReportNameEnum.TEAM_AMBASSADORS]),
  updatedAt: z.string(),
  userId: z.number(),
});

export const TeamUsersCsvSchema = CsvReportSchema.extend({
  jobs: z.object({
    [CsvJobNameKeyEnum.TEAM_USERS]: CsvJobSchema,
  }),
  name: ZodSchemaUtil.makeStringLiteralUnion([CsvReportNameEnum.TEAM_USERS, CsvReportNameEnum.ORG_TEAM_USERS]),
  updatedAt: z.string(),
  userId: z.number(),
});

export const OrgProjectsCsvSchema = CsvReportSchema.extend({
  jobs: z.object({
    [CsvJobNameKeyEnum.PROJECTS]: CsvJobSchema,
  }),
  name: ZodSchemaUtil.makeStringLiteralUnion([
    CsvReportNameEnum.ORG_PRE_SALES_PROJECTS,
    CsvReportNameEnum.ORG_POST_SALES_PROJECTS,
    CsvReportNameEnum.ORG_COMPLETED_PROJECTS,
  ]),
  updatedAt: z.string(),
  userId: z.number(),
});

export const GetTeamUsersCsvResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    csv: TeamUsersCsvSchema,
  }),
});

export const GetOrgProjectsCsvResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    csv: OrgProjectsCsvSchema,
  }),
});

export const GetAmbassadorsCsvResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    csv: TeamAmbassadorsCsvSchema,
  }),
});

export const GetAmbassadorsCsvParamsSchema = z.object({
  filterParams: AmbassadorFilterParamsSchema,
});

export const GetEarningsAnalyticsCsvParamsSchema = z.object({
  timeFrame: z.nativeEnum(EarningsAnalyticsTimeFrameEnum),
});

export const EarningsAnalyticsCsvSchema = CsvReportSchema.extend({
  jobs: z.object({
    [CsvJobNameKeyEnum.EARNINGS_ANALYTICS]: CsvJobSchema,
  }),
  name: z.string().regex(/^EarningsIntelligence-\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, {
    message: "Expected EarningsIntelligence-YYYY-MM-DD HH:MM:SS",
  }),
  updatedAt: z.string(),
  userId: z.number(),
});

export const GetEarningsAnalyticsCsvResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    csv: EarningsAnalyticsCsvSchema,
  }),
});

export const GetTeamIntelligenceCsvParamsSchema = z.object({
  filterParams: TeamMembersFilterParamsSchema.optional(),
});

export const TeamIntelligenceCsvSchema = CsvReportSchema.extend({
  jobs: z.object({
    [CsvJobNameKeyEnum.TEAM_INTELLIGENCE]: CsvJobSchema,
  }),
  name: ZodSchemaUtil.makeStringLiteralUnion([CsvReportNameEnum.TEAM_INTELLIGENCE]),
  updatedAt: z.string(),
  userId: z.number(),
});

export const GetTeamIntelligenceCsvResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    csv: TeamIntelligenceCsvSchema,
  }),
});
